import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Braces } from 'lucide-react';

/**
 * Text input with a "dynamic token" helper. Lets booth designers discover and
 * insert the `${...}` tokens understood by the booth runtime
 * (see ExperienceViewModel.replaceTokens) without memorising the syntax:
 *   - `${dateFormat|<pattern>}` formatted current date/time
 *   - `${random|format=...;charset=...;seed=...}` formatted random value
 *   - `${variableName}` a saved value captured earlier in the experience
 */

interface TokenizedTextInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Extra classes appended to the input element. */
    className?: string;
    /** Render a multi-line textarea instead of a single-line input. */
    multiline?: boolean;
}

const DATE_PRESETS: { label: string; pattern: string; example: string }[] = [
    { label: 'Date', pattern: 'yyyy-MM-dd', example: '2026-06-18' },
    { label: 'Time (24h)', pattern: 'HH:mm', example: '14:05' },
    { label: 'Time (12h)', pattern: 'h:mm a', example: '2:05 PM' },
    { label: 'Date & time', pattern: 'yyyy-MM-dd HH:mm', example: '2026-06-18 14:05' },
    { label: 'Year', pattern: 'yyyy', example: '2026' },
    { label: 'Weekday', pattern: 'EEEE', example: 'Thursday' },
    { label: 'Long date', pattern: 'MMM d, yyyy', example: 'Jun 18, 2026' },
];

type CharsetKey = 'uppercase' | 'lowercase' | 'numeric' | 'special';

const CHARSET_OPTIONS: { key: CharsetKey; label: string; chars: string }[] = [
    { key: 'uppercase', label: 'Uppercase', chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
    { key: 'lowercase', label: 'Lowercase', chars: 'abcdefghijklmnopqrstuvwxyz' },
    { key: 'numeric', label: 'Numbers', chars: '0123456789' },
    { key: 'special', label: 'Special', chars: '!@#$%^&*-_=+' },
];

// Lightweight 32-bit string hash + PRNG used purely to render an illustrative
// preview. The booth uses 64-bit SplitMix64 (see ExperienceViewModel.swift), so
// this shows the format/charset shape and seed stability rather than the exact
// characters the device will produce.
function hashString(value: string): number {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function mulberry32(seed: number): () => number {
    let state = seed;
    return () => {
        state = (state + 0x6d2b79f5) | 0;
        let t = Math.imul(state ^ (state >>> 15), 1 | state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function buildCharacterPool(selected: Set<CharsetKey>): string[] {
    const pool = new Set<string>();
    for (const option of CHARSET_OPTIONS) {
        if (selected.has(option.key)) {
            for (const char of option.chars) pool.add(char);
        }
    }
    // Sort to match Swift's `Set<Character>.sorted()`, so a seed maps to the
    // same characters on the device and in this preview.
    return Array.from(pool).sort();
}

function buildRandomToken(
    format: string,
    selected: Set<CharsetKey>,
    seed: string,
): string {
    const charset = CHARSET_OPTIONS.filter((option) =>
        selected.has(option.key),
    )
        .map((option) => option.key)
        .join(',');
    let token = `random|format=${format};charset=${charset}`;
    if (seed.trim()) token += `;seed=${seed.trim()}`;
    return `\${${token}}`;
}

function previewRandom(
    format: string,
    selected: Set<CharsetKey>,
    seed: string,
): string {
    const pool = buildCharacterPool(selected);
    if (pool.length === 0 || !format) return '';
    const rand = seed.trim()
        ? mulberry32(hashString(seed.trim()))
        : Math.random;
    let output = '';
    for (const char of format) {
        if (char === 'x' || char === 'X') {
            output += pool[Math.floor(rand() * pool.length)];
        } else {
            output += char;
        }
    }
    return output;
}

export default function TokenizedTextInput({
    value,
    onChange,
    placeholder,
    className = '',
    multiline = false,
}: TokenizedTextInputProps) {
    const [open, setOpen] = useState(false);
    const [customDateFormat, setCustomDateFormat] = useState('');
    const [randomFormat, setRandomFormat] = useState('xxxxxx-xx');
    const [randomSeed, setRandomSeed] = useState('');
    const [randomCharset, setRandomCharset] = useState<Set<CharsetKey>>(
        () => new Set<CharsetKey>(['uppercase', 'numeric']),
    );
    const [variableName, setVariableName] = useState('');
    // Fixed-position coordinates for the portaled panel so it is never clipped
    // by the property panel's `overflow-hidden` containers.
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(
        null,
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    // Remember the caret so tokens land where the user was typing rather than
    // always appending to the end.
    const selectionRef = useRef<{ start: number; end: number }>({
        start: 0,
        end: 0,
    });

    const PANEL_WIDTH = 320;

    const updateCoords = () => {
        const button = buttonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const left = Math.max(
            8,
            Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8),
        );
        setCoords({ top: rect.bottom + 4, left });
    };

    useEffect(() => {
        if (!open) return;
        updateCoords();
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                !containerRef.current?.contains(target) &&
                !panelRef.current?.contains(target)
            ) {
                setOpen(false);
            }
        };
        const handleReposition = () => updateCoords();
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [open]);

    const rememberSelection = () => {
        const el = inputRef.current;
        if (el) {
            selectionRef.current = {
                start: el.selectionStart ?? value.length,
                end: el.selectionEnd ?? value.length,
            };
        }
    };

    const insertToken = (token: string) => {
        const { start, end } = selectionRef.current;
        const safeStart = Math.min(start, value.length);
        const safeEnd = Math.min(end, value.length);
        const next =
            value.slice(0, safeStart) + token + value.slice(safeEnd);
        onChange(next);
        const caret = safeStart + token.length;
        selectionRef.current = { start: caret, end: caret };
        requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
                el.focus();
                el.setSelectionRange(caret, caret);
            }
        });
    };

    const randomPreview = useMemo(
        () => previewRandom(randomFormat, randomCharset, randomSeed),
        [randomFormat, randomCharset, randomSeed],
    );

    const toggleCharset = (key: CharsetKey) => {
        setRandomCharset((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const inputClasses = `w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`;

    return (
        <div ref={containerRef} className="relative">
            {multiline ? (
                <textarea
                    ref={inputRef}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onSelect={rememberSelection}
                    onKeyUp={rememberSelection}
                    onClick={rememberSelection}
                    onBlur={rememberSelection}
                    className={inputClasses}
                    placeholder={placeholder}
                    rows={3}
                />
            ) : (
                <input
                    ref={inputRef}
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onSelect={rememberSelection}
                    onKeyUp={rememberSelection}
                    onClick={rememberSelection}
                    onBlur={rememberSelection}
                    className={inputClasses}
                    placeholder={placeholder}
                />
            )}

            <button
                ref={buttonRef}
                type="button"
                title="Insert a dynamic token"
                onClick={() => {
                    rememberSelection();
                    setOpen((prev) => !prev);
                }}
                className={`absolute right-1.5 ${
                    multiline ? 'top-2' : 'top-1.5'
                } flex items-center justify-center w-6 h-6 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors`}
            >
                <Braces className="w-4 h-4" />
            </button>

            {open &&
                coords &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            width: PANEL_WIDTH,
                        }}
                        className="z-50 max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg p-3 space-y-4"
                    >
                        <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-900">
                            Insert dynamic token
                        </span>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-xs text-gray-400 hover:text-gray-600"
                        >
                            Done
                        </button>
                    </div>

                    {/* Date & time */}
                    <div className="space-y-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Date &amp; time
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {DATE_PRESETS.map((preset) => (
                                <button
                                    key={preset.pattern}
                                    type="button"
                                    title={`Example: ${preset.example}`}
                                    onClick={() =>
                                        insertToken(
                                            `\${dateFormat|${preset.pattern}}`,
                                        )
                                    }
                                    className="px-2 py-1 text-xs rounded border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-1">
                            <input
                                type="text"
                                value={customDateFormat}
                                onChange={(e) =>
                                    setCustomDateFormat(e.target.value)
                                }
                                placeholder="Custom format e.g. dd/MM/yy"
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <button
                                type="button"
                                disabled={!customDateFormat.trim()}
                                onClick={() => {
                                    insertToken(
                                        `\${dateFormat|${customDateFormat.trim()}}`,
                                    );
                                    setCustomDateFormat('');
                                }}
                                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
                            >
                                Insert
                            </button>
                        </div>
                    </div>

                    {/* Random value */}
                    <div className="space-y-2 border-t border-gray-100 pt-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Random value
                        </div>
                        <div>
                            <label className="block text-[11px] text-gray-600 mb-0.5">
                                Format (each{' '}
                                <code className="px-1 bg-gray-100 rounded">
                                    x
                                </code>{' '}
                                becomes a random character)
                            </label>
                            <input
                                type="text"
                                value={randomFormat}
                                onChange={(e) =>
                                    setRandomFormat(e.target.value)
                                }
                                placeholder="xxxxxx-xx"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {CHARSET_OPTIONS.map((option) => (
                                <label
                                    key={option.key}
                                    className="flex items-center gap-1 text-xs text-gray-700"
                                >
                                    <input
                                        type="checkbox"
                                        checked={randomCharset.has(option.key)}
                                        onChange={() =>
                                            toggleCharset(option.key)
                                        }
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                        <div>
                            <label className="block text-[11px] text-gray-600 mb-0.5">
                                Seed (optional — same seed always gives the same
                                value)
                            </label>
                            <input
                                type="text"
                                value={randomSeed}
                                onChange={(e) => setRandomSeed(e.target.value)}
                                placeholder="Leave empty for fully random"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-500 truncate">
                                {randomPreview ? (
                                    <>
                                        Example:{' '}
                                        <code className="px-1 bg-gray-100 rounded text-gray-800">
                                            {randomPreview}
                                        </code>
                                    </>
                                ) : (
                                    'Select at least one character set'
                                )}
                            </span>
                            <button
                                type="button"
                                disabled={
                                    randomCharset.size === 0 ||
                                    !randomFormat.trim()
                                }
                                onClick={() =>
                                    insertToken(
                                        buildRandomToken(
                                            randomFormat,
                                            randomCharset,
                                            randomSeed,
                                        ),
                                    )
                                }
                                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40 flex-shrink-0"
                            >
                                Insert
                            </button>
                        </div>
                    </div>

                    {/* Saved variable */}
                    <div className="space-y-2 border-t border-gray-100 pt-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Saved variable
                        </div>
                        <p className="text-[11px] text-gray-500">
                            Inserts a value captured earlier in the experience
                            (e.g. an input field's key).
                        </p>
                        <div className="flex gap-1">
                            <input
                                type="text"
                                value={variableName}
                                onChange={(e) =>
                                    setVariableName(e.target.value)
                                }
                                placeholder="variable name e.g. name"
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <button
                                type="button"
                                disabled={!variableName.trim()}
                                onClick={() => {
                                    insertToken(`\${${variableName.trim()}}`);
                                    setVariableName('');
                                }}
                                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
                            >
                                Insert
                            </button>
                        </div>
                    </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
